<template>
  <div
    ref="fileDropArea"
    class="form-group"
  >
    <div class="form-group-label attachments">
      <label for="attachments">Attachments <span class="file-size-limit">(Up to 3 files)</span></label>
      <div class="file-size-limit">
        <span>Max file size: 5MB</span>
      </div>
    </div>
    <div class="file-list">
      <div
        v-for="(file, index) in modelValue"
        :key="file.name"
        class="file-item"
      >
        <div class="file-item-name-and-icon">
          <Icon
            name="lucide:file"
            size="14"
          />
          {{ file.name }}
        </div>

        <button @click="removeFile(index)">
          <Icon
            name="lucide:x"
            size="14"
            background-color="#BD3324"
          />
        </button>
      </div>
    </div>
    <div
      v-if="modelValue.length < 3 && !fileUploadError"
      id="attachments"
      class="drop-area"
      :style="{ 'background-color': highlightColor }"
      @dragenter.prevent="highlight"
      @dragover.prevent="highlight"
      @dragleave.prevent="unhighlight"
      @drop="handleDropOrUploadFile($event, true)"
    >
      <div class="can-add-files">
        <button
          class="add-files-button"
          @click="openFileModal"
        >
          <Icon
            name="lucide:upload"
            size="14"
            color="#FFF"
          />Upload
        </button>
        or drop here
        <input
          ref="upload"
          type="file"
          hidden
          multiple
          :accept="acceptFileTypes?.join(',') ?? ''"
          @change="handleDropOrUploadFile"
        >
      </div>
    </div>
    <div
      v-if="fileSizeExceeded || fileUploadError"
      class="file-size-exceeded"
    >
      <span>{{ fileSizeExceeded ? "File size exceeded" : "Max files reached" }}</span>
    </div>
    <div
      v-else-if="fileTypeError"
      class="file-type-error"
    >
      <span>{{ fileTypeErrorMessage }}</span>
    </div>
  </div>
</template>

<script setup>
import { ref, useTemplateRef } from "vue";
defineOptions({
  name: "FileAttachments"
});
const props = defineProps({
  modelValue: { type: Array, required: false, default: () => [] },
  acceptFileTypes: { type: Array, required: false, default: () => ["image/png", "image/jpeg", "image/gif", "application/pdf"] },
  fileTypeErrorMessage: { type: String, required: false, default: "File type not supported. Please upload a PNG, JPG, GIF, or PDF." }
});
const emit = defineEmits(["update:modelValue"]);
const upload = useTemplateRef("upload");
const fileUploadError = ref(false);
const fileSizeExceeded = ref(false);
const fileTypeError = ref(false);
const highlightColor = ref("#FFF");
function highlight() {
  highlightColor.value = "#1850C11A";
}
function unhighlight() {
  highlightColor.value = "#FFF";
}
function openFileModal() {
  upload.value?.click();
}
function removeFile(index) {
  const newFiles = [...props.modelValue];
  newFiles.splice(index, 1);
  emit("update:modelValue", newFiles);
  if (newFiles.length < 3) {
    fileSizeExceeded.value = false;
    fileUploadError.value = false;
    fileTypeError.value = false;
  }
}
async function handleDropOrUploadFile(event, isDropEvent = false) {
  unhighlight();
  fileUploadError.value = false;
  fileSizeExceeded.value = false;
  fileTypeError.value = false;
  event.preventDefault();
  const files = isDropEvent ? event.dataTransfer?.files : event.target.files;
  if (!files) return;
  const acceptedFileTypes = ["image/png", "image/jpeg", "image/gif", "image/jpg", "application/pdf"];
  const newFiles = [...props.modelValue];
  Array.from(files).forEach((file) => {
    const totalFileNum = newFiles.length + 1;
    if (totalFileNum > 3) {
      fileUploadError.value = true;
      return;
    }
    if (!acceptedFileTypes.includes(file.type)) {
      fileTypeError.value = true;
      return;
    }
    if (file.size > 5e6) {
      fileSizeExceeded.value = true;
      return;
    }
    newFiles.push(file);
  });
  emit("update:modelValue", newFiles);
}
</script>

<style scoped>
.form-group{--border-radius-xs:4px;--border-radius-full:9999px;--button-gap:7px;--button-padding-y:7px;--button-padding-x:10.5px;--color-border-default:#adadad;--color-blue-500:#1850c1;--color-grey-950:#2d2d2d;--color-text-secondary:#616161;--color-text-severity-danger-on-bg:#9b2a1d;--color-text-severity-danger-default:#bd3324;--color-white:#fff;--fileupload-header-padding:15.75px;--fileupload-header-gap:7px;display:flex;flex-direction:column;gap:6px;position:relative}.form-group-label.attachments{align-items:center;display:flex;flex-direction:row;justify-content:space-between}.form-group-label label{color:var(--color-grey-950);font-family:Inter;font-size:16px;font-style:normal;font-weight:400;line-height:22px;width:-moz-fit-content;width:fit-content}.drop-area{align-items:center;align-self:stretch;border:1px solid var(--color-border-default);border-radius:var(--border-radius-xs);color:var(--color-text-secondary);display:flex;font-size:14px;font-style:normal;font-weight:400;gap:var(--fileupload-header-gap);line-height:18px;padding:var(--fileupload-header-padding)}.file-size-exceeded,.file-type-error{color:var(--color-text-severity-danger-default);font-family:Inter;font-size:12px;font-weight:400;letter-spacing:.4px}.can-add-files{align-items:center;display:flex;flex-direction:row;gap:8px}.add-files-button{align-items:center;background:var(--color-blue-500);border:1px solid var(--color-blue-500);border-radius:var(--border-radius-full);color:var(--color-white);cursor:pointer;display:flex;gap:var(--button-gap);opacity:.7;padding:var(--button-padding-y) var(--button-padding-x)}.add-files-button:focus-visible{outline:1px solid var(--color-blue-500);outline-offset:1px}.file-size-limit{color:var(--color-text-secondary);font-size:14px;font-weight:400;line-height:18px;text-align:right}.file-list{display:flex;flex-direction:column;gap:8px}.file-item{align-items:center;border:1px solid var(--color-border-default);border-radius:var(--border-radius-xs);display:flex;justify-content:space-between;line-height:18px;padding:12px}.file-item-name-and-icon{align-items:center;display:flex;gap:4px;word-break:break-all}.file-item-name-and-icon svg{color:#949494}.file-item button{align-items:center;background:none;border:none;cursor:pointer;display:flex}
</style>
